import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import LanguageContext from "context/language/languageContext";
import { isEnglish } from "shared/functions";
import { useDateTime } from "shared/hooks/useDateTime";
import { useOverflow } from "shared/hooks/useOverflow";
import { body3Regular } from "shared/styles/typography";
import { LOCALES, USER_STATUSES } from "shared/types/enums";

import { DATE_LABELS } from "./constants";
import { DateLabel, UserStatusProps } from "./types";

const UserStatus = ({
    status,
    activeExpiresAt,
    invitation
}: UserStatusProps) => {
    const { gray500, gray700, green700, orange600, textPlaceholder, white } =
        colors;

    const { language } = useContext(LanguageContext);

    const { t } = useTranslation();
    const { formattedDate } = useDateTime();

    const { ref: statusRef, isOverflowing: isStatusOverflowing } =
        useOverflow();

    const { ref: dateRef, isOverflowing: isDateOverflowing } = useOverflow();

    const statusIcon = {
        [USER_STATUSES.Active]: {
            bgColor: green700,
            borderColor: green700
        },
        [USER_STATUSES.Inactive]: {
            bgColor: white,
            borderColor: gray500
        },
        [USER_STATUSES.InvitationExpired]: {
            bgColor: orange600,
            borderColor: orange600
        },
        [USER_STATUSES.InvitationPending]: {
            bgColor: textPlaceholder,
            borderColor: textPlaceholder
        }
    };

    const getDateLabel = (label: DateLabel, date?: string | null) =>
        date ? `${t(label)} ${formattedDate(date, "ll", locale)}` : undefined;

    const renderDateLabel = () => {
        switch (status) {
            case USER_STATUSES.Active:
                return (
                    getDateLabel(DATE_LABELS.Expires, activeExpiresAt) ??
                    t(DATE_LABELS.NeverExpires)
                );
            case USER_STATUSES.Inactive:
                return getDateLabel(DATE_LABELS.Expired, activeExpiresAt);
            case USER_STATUSES.InvitationExpired: {
                return getDateLabel(
                    DATE_LABELS.Expired,
                    invitation?.expires_at
                );
            }
            case USER_STATUSES.InvitationPending: {
                return getDateLabel(DATE_LABELS.Sent, invitation?.created_at);
            }
            default:
                return undefined;
        }
    };

    const bgColor = statusIcon[status].bgColor;
    const borderColor = statusIcon[status].borderColor;

    const isEnglishLng = isEnglish(language);
    const locale = isEnglishLng ? LOCALES.English : undefined;

    return (
        <div>
            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    columnGap: "8px"
                })}
            >
                <span
                    css={css({
                        display: "block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        boxSizing: "border-box",
                        background: bgColor,
                        border: `1px solid ${borderColor}`
                    })}
                />

                <TooltipGeneral
                    title={t(`Table##user statuses##${status}`)}
                    disableHoverListener={!isStatusOverflowing}
                    placement={TOOLTIP_PLACEMENT.Top}
                    removeArrow
                >
                    <span
                        ref={statusRef}
                        css={css({
                            overflowX: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: "1"
                        })}
                    >
                        {t(`Table##user statuses##${status}`)}
                    </span>
                </TooltipGeneral>
            </div>

            <TooltipGeneral
                title={renderDateLabel()}
                disableHoverListener={!isDateOverflowing}
                placement={TOOLTIP_PLACEMENT.Top}
                removeArrow
            >
                <div
                    ref={dateRef}
                    css={[
                        body3Regular,
                        {
                            color: gray700,
                            overflowX: "hidden",
                            textOverflow: "ellipsis"
                        }
                    ]}
                >
                    {renderDateLabel()}
                </div>
            </TooltipGeneral>
        </div>
    );
};

export default UserStatus;
